import { Fade, Grid, Theme, Typography, useMediaQuery } from "@mui/material"
import { BorrowAndLendingMobileTable, BorrowAndLendingTable, BorrowCapacityStats, HeathFactorStats } from "../components"
import StatsBox, { StatsBoxType } from "../components/commons/StatsBox"
import { Button } from "../components/commons"
import { useNavigate } from "react-router"
import { siteMap } from "../components/commons/constants"
import useGlobalStore from "../stores/global"

const statsBoxPlaceHolder: StatsBoxType[] = [
  { title: 'My Deposits', content: '0', type: 'dollar' },
  { title: 'My Debt', content: '0', type: 'dollar' },
  { title: 'Net APY', content: '0', type: 'percent', tooltip: 'The annual percentage yield of all your lend and borrow amount. This value is dynamic and can fluctuate over time based on the utilisation rate of each pool.' },
  { title: 'Health Factor', content: '0', type: 'percent', tooltip: 'Your Health Factor represents the overall stability of your loan. It is calculated by comparing the value of your total deposits with your total debt. Your Health Factor is also influenced by your Loan-to-Value (LTV) ratio. A higher Health Factor indicates a lower risk of liquidation, reflecting a more secure position relative to your loan and collateral.' },
  { title: 'My Borrow Capacity', content: '0', subContent: '0', type: 'dollar', tooltip: 'The maximum amount you can borrow based on your current collateral and health factor.' },
]

const Dashboard = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const depositAndBorrowData = useGlobalStore((state) => state.userBorrowAndDepositData)
  const userBorrowAndDepositData = useGlobalStore((state) => state.userBorrowAndDepositData)
  const userAccountData = useGlobalStore((state) => state.userAccountData)
  const navigate = useNavigate()
  const statsBox: StatsBoxType[] = userBorrowAndDepositData.length && userAccountData ? statsBoxPlaceHolder.map((stats) => {
    let content: number | string = '0'
    let subContent: number | string = '0'
    const totalBorrow = userBorrowAndDepositData.reduce((acc, token) => acc + (Number(token.borrow) * Number(token.price)), 0)
    switch (stats.title) {
      case 'My Deposits':
        content = userAccountData.totalCollateralBase
        // content = userBorrowAndDepositData.reduce((acc, token) => acc + (Number(token.supply) * Number(token.price)), 0)
        break;
      case 'My Debt':
        content = userAccountData.totalDebtBase
        // content = userBorrowAndDepositData.reduce((acc, token) => acc + (Number(token.borrow) * Number(token.price)), 0)
        break;
      case 'Net APY':
        const totalSupply = userBorrowAndDepositData.reduce((acc, token) => acc + (Number(token.supply) * Number(token.price)), 0)
        const totalSupplyApy = userBorrowAndDepositData.reduce((acc, token) => acc + ((Number(token.supplyApy) / 100) * Number(token.supply) * Number(token.price)), 0)
        const totalBorrowApy = userBorrowAndDepositData.reduce((acc, token) => acc + ((Number(token.borrowApy) / 100) * Number(token.borrow) * Number(token.price)), 0)
        const netSupplyApy = (totalSupply > 0 && totalSupplyApy > 0 ? (totalSupplyApy / totalSupply) : 0)
        const netBorrowApy = (totalBorrow > 0 && totalBorrowApy > 0 ? (totalBorrowApy / totalBorrow) : 0)
        content = (netSupplyApy - netBorrowApy) * 100
        break;
      case 'Health Factor':
        content = Math.min(userAccountData?.healthFactor ?? 0, 10)
        break;
      case 'My Borrow Capacity':
        content = totalBorrow
        subContent = userAccountData?.availableBorrowsBase + totalBorrow
        return { ...stats, content: content, subContent: subContent }
      default:
        break;
    }
    return { ...stats, content: content }
  }) : statsBoxPlaceHolder

  return (
    <Grid container rowGap={{ xs: 2, sm: 4 }}>
      {
        isMobile ?
          <Grid item container justifyContent={'space-between'} alignItems={'center'} spacing={2}>
            <Grid item container flexDirection={'row'} justifyContent={'space-between'}>
              <Typography variant="h4">My Portfolio</Typography>
              <Button color="primary" size="small" variant='contained' onClick={() => navigate(siteMap.markets.path)}>
                All Markets
              </Button>
            </Grid>
            <Grid item >
              <Typography variant="r" color={'text.primary'}>
                View all your positions, deposit/ withdraw assets in this dashboard.
              </Typography>
            </Grid>
          </Grid>
          :
          <Grid item container justifyContent={'space-between'} alignItems={'center'} gap={2}>
            <Fade in timeout={400}>
              <Grid item>
                <Typography variant="h4">My Portfolio</Typography>
                <Typography variant="h6">View all your positions, deposit/withdraw assets in this dashboard.</Typography>
              </Grid>
            </Fade>
            <Fade in timeout={1200}>
              <Grid item >
                <Button color="primary" size="large" variant='contained' onClick={() => navigate(siteMap.markets.path)}>
                  All Markets
                </Button>
              </Grid>
            </Fade>
          </Grid>
      }
      <Grid item container spacing={{ xs: 1.2, sm: 3 }}>
        {


          statsBox.map((stats, index) => {
            if (stats.title === 'Health Factor') return <Fade in={true} timeout={400 * (index + 1)}><Grid item container width={'100%'} xs={6} sm={6} lg={3}><HeathFactorStats title={stats.title} health={Number(stats.content)} /></Grid></Fade>
            if (stats.title === 'My Borrow Capacity') return <Fade in={true} timeout={400 * (index + 1)}><Grid item container width={'100%'} xs={12} sm={6} lg={3}><BorrowCapacityStats title={stats.title} borrow={Number(stats.content)} borrowCap={Number(stats.subContent)} /></Grid></Fade>
            return <Fade in={true} timeout={400 * (index + 1)}>
              <Grid item container width={'100%'} xs={6} sm={4} lg={2}><StatsBox key={stats.title} {...stats} /></Grid>
            </Fade>
          }
          )
        }
        <Grid item container spacing={3}>
          {
            isMobile ? <Grid item xs={12}>
              <BorrowAndLendingMobileTable tabs={['My Deposits', 'My Borrows']} data={depositAndBorrowData} />
            </Grid> :
              <>
                <Fade in timeout={800}>
                  <Grid item xs={6}>
                    <BorrowAndLendingTable title={'Deposited'} data={depositAndBorrowData} />
                  </Grid>
                </Fade>
                <Fade in timeout={800}>
                  <Grid item xs={6}>
                    <BorrowAndLendingTable title={'Borrowed'} data={depositAndBorrowData} />
                  </Grid>
                </Fade>
              </>
          }
        </Grid>
      </Grid>
    </Grid>
  )
}

export default Dashboard